import dayjs from 'dayjs';
import i18next, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import locales from './locales';

export const resources: { [key: string]: Resource } = {
  ar: locales.arAR,
  'ar-AR': locales.arAR,
  cs: locales.csCS,
  'cs-CS': locales.csCS,
  da: locales.daDK,
  'da-DK': locales.daDK,
  de: locales.deDE,
  'de-DE': locales.deDE,
  el: locales.elGR,
  'el-GR': locales.elGR,
  'en-GB': locales.enGB,
  es: locales.esES,
  'es-ES': locales.esES,
  fi: locales.fiFI,
  'fi-FI': locales.fiFI,
  fr: locales.frFR,
  'fr-FR': locales.frFR,
  he: locales.heIL,
  'he-IL': locales.heIL,
  hu: locales.huHU,
  'hu-HU': locales.huHU,
  id: locales.idID,
  'id-ID': locales.idID,
  it: locales.itIT,
  'it-IT': locales.itIT,
  ja: locales.jaJA,
  'ja-JA': locales.jaJA,
  ko: locales.koKO,
  'ko-KO': locales.koKO,
  ms: locales.msMY,
  'ms-BN': locales.msMY,
  'ms-MY': locales.msMY,
  nl: locales.nlNL,
  'nl-BE': locales.nlBE,
  'nl-NL': locales.nlNL,
  no: locales.noNO,
  'no-NO': locales.noNO,
  pl: locales.plPL,
  'pl-PL': locales.plPL,
  pt: locales.ptPT,
  'pt-BR': locales.ptBR,
  'pt-PT': locales.ptPT,
  ru: locales.ruRU,
  'ru-RU': locales.ruRU,
  sk: locales.skSK,
  'sk-SK': locales.skSK,
  sl: locales.slSL,
  'sl-SL': locales.slSL,
  sv: locales.svSE,
  'sv-SE': locales.svSE,
  th: locales.thTH,
  'th-TH': locales.thTH,
  tr: locales.trTR,
  'tr-TR': locales.trTR,
  zh: locales.zhCN,
  'zh-CN': locales.zhCN,
  bg: locales.bgBG,
  'bg-BG': locales.bgBG,
  ro: locales.roRO,
  'ro-RO': locales.roRO,
  uk: locales.uk,
  vi: locales.vi,
};

/**
 * Maintaing a map of language imports for dayjs so vite will bundle files
 * Chunks will be dynamically loaded at runtime
 */
const dayJSLocaleImportMap: Record<
  string,
  () => Promise<Record<string, unknown>>
> = {
  ar: async () => import('dayjs/locale/ar'),
  cs: async () => import('dayjs/locale/cs'),
  da: async () => import('dayjs/locale/da'),
  de: async () => import('dayjs/locale/de'),
  el: async () => import('dayjs/locale/el'),
  en: async () => import('dayjs/locale/en'),
  'en-gb': async () => import('dayjs/locale/en-gb'),
  es: async () => import('dayjs/locale/es'),
  'es-mx': async () => import('dayjs/locale/es-mx'),
  fi: async () => import('dayjs/locale/fi'),
  fr: async () => import('dayjs/locale/fr'),
  he: async () => import('dayjs/locale/he'),
  hu: async () => import('dayjs/locale/hu'),
  id: async () => import('dayjs/locale/id'),
  it: async () => import('dayjs/locale/it'),
  ja: async () => import('dayjs/locale/ja'),
  ko: async () => import('dayjs/locale/ko'),
  ms: async () => import('dayjs/locale/ms'),
  nl: async () => import('dayjs/locale/nl'),
  no: async () => import('dayjs/locale/nb'),
  nb: async () => import('dayjs/locale/nb'),
  nn: async () => import('dayjs/locale/nn'),
  pl: async () => import('dayjs/locale/pl'),
  pt: async () => import('dayjs/locale/pt'),
  'pt-br': async () => import('dayjs/locale/pt-br'),
  ro: async () => import('dayjs/locale/ro'),
  ru: async () => import('dayjs/locale/ru'),
  sk: async () => import('dayjs/locale/sk'),
  sl: async () => import('dayjs/locale/sl'),
  sv: async () => import('dayjs/locale/sv'),
  th: async () => import('dayjs/locale/th'),
  tr: async () => import('dayjs/locale/tr'),
  uk: async () => import('dayjs/locale/uk'),
  vi: async () => import('dayjs/locale/vi'),
  zh: async () => import('dayjs/locale/zh'),
  'zh-cn': async () => import('dayjs/locale/zh-cn'),
};

export const locale =
  (window.navigator as Navigator & { userLanguage: string }).userLanguage ||
  window.navigator.language;

const importLang =
  locale.toLocaleLowerCase() === 'zh' ? 'zh-cn' : locale.toLocaleLowerCase();

export const initialise = async () => {
  await i18next.use(initReactI18next).init({
    fallbackLng: ['en-GB'],
    lng: locale,
    resources,
    interpolation: {
      skipOnVariables: false,
    },
    react: {
      // i18next is initialised outside React (at app start) and the app has no
      // <Suspense> boundary. Disable suspense so useTranslation() never throws
      // while loading; it returns a working t() that falls back to the key until
      // ready, matching the existing i18next.t() behaviour.
      useSuspense: false,
    },
  });

  document.body.dir = i18next.dir();

  try {
    const lang = importLang.split('-')[0];

    if (dayJSLocaleImportMap[importLang]) {
      await dayJSLocaleImportMap[importLang]();
      dayjs.locale(importLang);
    } else if (lang && dayJSLocaleImportMap[lang]) {
      await dayJSLocaleImportMap[lang]();
      dayjs.locale(lang);
    } else {
      dayjs.locale('en-gb');
    }
  } catch (err) {
    dayjs.locale('en-gb');
    return;
  }
};

export default i18next;
